import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError';
import { query, writeQuery } from '@confluence/query-preloader-tools/entry-points/graphql';
import { preloadFooterCreateButton } from '@confluence/create-blank-fabric-page/entry-points/preloadFooterCreateButton';

import type {
	PageTreeQuery as PageTreeQueryType,
	PageTreeQueryVariables,
} from './queries/__types__/PageTreeQuery';
import { INITIAL_PAGE_SIZE } from './paginationLimits';
import type { GraphQLPageStatus } from './pageTreeStatuses';
import { PageTreeQuery, PageTreeWithSpaceKeyQuery } from './queries/PageTreeQuery.graphql';
import type {
	PageTreeWithSpaceKeyQuery as PageTreeWithSpaceKeyQueryType,
	PageTreeWithSpaceKeyQueryVariables,
} from './queries/__types__/PageTreeWithSpaceKeyQuery';

export type preloadPageTreeProps = {
	spaceKey: string;
	statuses: GraphQLPageStatus[];
	isSuperAdmin: boolean;
	contentId?: number | string;
};

export async function preloadPageTree({
	spaceKey,
	statuses,
	isSuperAdmin,
	contentId = '',
}: preloadPageTreeProps): Promise<any> {
	const promises: Promise<any>[] = [
		preloadPageTreeContent({ spaceKey, statuses, isSuperAdmin, contentId }),
		preloadFooterCreateButton(spaceKey),
	];
	return waitForAllReturnWhenError(promises);
}

export async function preloadPageTreeContent({
	spaceKey,
	statuses,
	isSuperAdmin,
	contentId = '',
}: preloadPageTreeProps) {
	// only fetch using PageTreeWithSpaceKeyQuery if contentId does not exist AND FF is enabled (only for SSR space overviews).
	// This is to differentiate between other use cases where contentId is not passed (according to prop types, it is an optional param)
	if (spaceKey && !contentId) {
		const { data } = await query<PageTreeWithSpaceKeyQueryType, PageTreeWithSpaceKeyQueryVariables>(
			{
				/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
				query: PageTreeWithSpaceKeyQuery,
				variables: {
					spaceKey,
					isSuperAdmin,
					statuses,
					// @ts-ignore
					ptStatuses: statuses,
					paginationLimit: INITIAL_PAGE_SIZE,
					isReplacePagesWithPTPageEnabled: true,
				},
			},
		);

		if (data?.space?.homepageId && data?.space?.id) {
			writeQuery<PageTreeQueryType, PageTreeQueryVariables>({
				query: PageTreeQuery,
				variables: {
					spaceKey,
					isSuperAdmin,
					statuses,
					// @ts-ignore
					ptStatuses: statuses,
					contentId: data?.space?.homepageId,
					paginationLimit: INITIAL_PAGE_SIZE,
					isReplacePagesWithPTPageEnabled: true,
				},
				data: {
					currentPage: data?.space?.pageTree || null,
					ptpage: data?.ptpage || null,
					space: data?.space
						? {
								id: data?.space?.id,
								homepage: {
									id: data?.space?.homepageId,
									// @ts-ignore
									__typename: 'Content',
								},
								operations: data?.space?.operations,
								// @ts-ignore
								__typename: 'Space',
							}
						: null,
				},
			});
		}
	} else {
		await query({
			/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
			query: PageTreeQuery,
			variables: {
				spaceKey,
				isSuperAdmin,
				statuses,
				ptStatuses: statuses,
				...(contentId && { contentId }),
				paginationLimit: INITIAL_PAGE_SIZE,
				isReplacePagesWithPTPageEnabled: true,
			},
		});
	}
}
